import { readSession } from "@/lib/backend";
import { useCallback, useEffect, useRef, useState } from "react";
import { haptics } from "@/lib/haptics";
import { pullArmed, pullAxis, pullRelease, type PullAxis } from "@/lib/pull_sync";
import { getSyncStatus, sync } from "@/lib/sync";

function isOnline(): boolean { return navigator.onLine; }
export const SYNC_STATUS_MS = 4000;
const OFFLINE_MESSAGE = "You're offline — everything is saved on this device";
/** Only vertical drags starting at the top belong to sync; horizontal rows keep their pointer. */
export function usePullSync(enabled = true): {
  message: string | null;
  tone: "normal" | "success" | "error";
  handlers: React.HTMLAttributes<HTMLDivElement>;
  host: React.RefObject<HTMLDivElement | null>;
} {
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<"normal" | "success" | "error">("normal");
  const host = useRef<HTMLDivElement>(null);
  const busy = useRef(false);
  const swallowClick = useRef(false);
  const drag = useRef<{ id: number; x: number; y: number; axis: PullAxis; distance: number; armed: boolean; owner: Element | null } | null>(null);
  useEffect(() => {
    if (message !== "Synced just now") return;
    const timeout = window.setTimeout(() => setMessage(null), SYNC_STATUS_MS);
    return () => window.clearTimeout(timeout);
  }, [message]);
  useEffect(() => {
    const online = (): void => setMessage(current => current === OFFLINE_MESSAGE ? null : current);
    window.addEventListener("online", online);
    return () => window.removeEventListener("online", online);
  }, []);
  const accepts = (target: EventTarget | null): boolean => target instanceof Element && !!host.current?.contains(target) && !target.closest("input, textarea, select, [contenteditable]");
  const run = useCallback(async (): Promise<void> => {
    if (busy.current) return;
    if (!isOnline()) { setMessage(OFFLINE_MESSAGE); setTone("normal"); return; }
    busy.current = true; setMessage("Syncing…"); setTone("normal");
    try {
      await sync();
      const status = getSyncStatus();
      if (status.state === "offline" || !isOnline()) { setMessage(OFFLINE_MESSAGE); return; }
      if (status.unreadable > 0) { setTone("error"); setMessage(`${String(status.unreadable)} ${status.unreadable === 1 ? "row" : "rows"} could not be read`); return; }
      if (status.error || status.state === "signed-out" || readSession() === null) {
        setTone("error"); setMessage(status.error ?? "Sign in again to sync. Everything is saved on this device."); haptics("error");
      } else if (status.held > 0) {
        setTone("normal"); setMessage(`${String(status.held)} ${status.held === 1 ? "change" : "changes"} waiting for a plan. Saved on this device; open Settings to resume uploading.`);
      } else { setTone("success"); setMessage("Synced just now"); haptics("success"); }
    } catch (cause: unknown) {
      setTone("error"); setMessage(cause instanceof Error ? cause.message : "Couldn't sync. Pull down to try again."); haptics("error");
    } finally { busy.current = false; }
  }, []);
  useEffect(() => {
    const node = host.current;
    if (!node || !enabled) return;
    let touch: { id: number; x: number; y: number; axis: PullAxis; distance: number; armed: boolean; owner: Element | null } | null = null;
    const start = (event: TouchEvent): void => {
      if (!accepts(event.target) || busy.current || event.touches.length !== 1 || window.scrollY > 0) return;
      let target = event.target instanceof HTMLElement ? event.target : null;
      while (target) { if (target.scrollTop > 0) return; target = target.parentElement; }
      drag.current = null; // Native touch events own scrolling when both event APIs fire.
      const point = event.touches[0];
      if (point) touch = { id: point.identifier, x: point.clientX, y: point.clientY, axis: "pending", distance: 0, armed: false, owner: event.target instanceof Element ? event.target.closest("[data-swipe-claim]") : null };
    };
    const move = (event: TouchEvent): void => {
      if (!touch) return;
      const point = Array.from(event.touches).find(point => point.identifier === touch?.id);
      if (!point || event.touches.length !== 1) { touch = null; return; }
      if (touch.owner?.hasAttribute("data-swipe-horizontal")) { touch.axis = "cancelled"; touch.distance = 0; setMessage(null); return; }
      touch.axis = pullAxis(point.clientX - touch.x, point.clientY - touch.y, touch.axis);
      if (touch.axis !== "vertical") return;
      event.preventDefault();
      touch.distance = Math.max(0, point.clientY - touch.y);
      const armed = pullArmed(touch.distance);
      if (armed && !touch.armed) haptics("select");
      touch.armed = armed;
      setTone("normal"); setMessage(armed ? "Release to sync" : "Pull to sync");
    };
    const end = (event: TouchEvent): void => {
      if (!touch || !Array.from(event.changedTouches).some(point => point.identifier === touch?.id)) return;
      const state = touch; touch = null;
      if (state.axis === "vertical") { swallowClick.current = true; window.setTimeout(() => { swallowClick.current = false; }, 0); }
      if (pullRelease(state.axis, state.distance, event.type === "touchcancel")) void run();
      else if (state.axis === "vertical") setMessage(null);
    };
    node.addEventListener("touchstart", start, { passive: true });
    node.addEventListener("touchmove", move, { passive: false });
    node.addEventListener("touchend", end); node.addEventListener("touchcancel", end);
    return () => {
      node.removeEventListener("touchstart", start); node.removeEventListener("touchmove", move);
      node.removeEventListener("touchend", end); node.removeEventListener("touchcancel", end);
    };
  }, [enabled, run]);
  return { message, tone, host, handlers: {
    onClickCapture: event => { if (!busy.current && (message === "Synced just now" || message === OFFLINE_MESSAGE)) setMessage(null); if (swallowClick.current) { event.preventDefault(); event.stopPropagation(); } },
    onPointerDownCapture: (event) => {
      if (!busy.current && (message === "Synced just now" || message === OFFLINE_MESSAGE)) setMessage(null);
      if (!accepts(event.target)) return;
      if (event.pointerType !== "touch" || !enabled || busy.current || !event.isPrimary || window.scrollY > 0) return;
      let node: HTMLElement | null = event.target instanceof HTMLElement ? event.target : null;
      while (node) { if (node.scrollTop > 0) return; node = node.parentElement; }
      drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: "pending", distance: 0, armed: false, owner: event.target instanceof Element ? event.target.closest("[data-swipe-claim]") : null };
    },
    // Bubble after the row has decided whether to lock horizontally.
    onPointerMove: (event) => {
      const state = drag.current;
      if (!state || state.id !== event.pointerId) return;
      if (state.owner?.hasAttribute("data-swipe-horizontal")) { state.axis = "cancelled"; state.distance = 0; setMessage(null); return; }
      state.axis = pullAxis(event.clientX - state.x, event.clientY - state.y, state.axis);
      if (state.axis !== "vertical") return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      state.distance = Math.max(0, event.clientY - state.y);
      const armed = pullArmed(state.distance);
      if (armed && !state.armed) haptics("select");
      state.armed = armed;
      setTone("normal"); setMessage(armed ? "Release to sync" : "Pull to sync");
    },
    onPointerUpCapture: (event) => {
      const state = drag.current;
      if (!state || state.id !== event.pointerId) return;
      drag.current = null;
      if (state.axis === "vertical") { swallowClick.current = true; window.setTimeout(() => { swallowClick.current = false; }, 0); }
      if (pullRelease(state.axis, state.distance, false)) void run();
      else if (state.axis === "vertical") setMessage(null);
    },
    onPointerCancelCapture: () => { drag.current = null; if (!busy.current) setMessage(null); },
  } };
}
